import { useState, useEffect } from "react";
import api from "../api/axios";
import styles from "./ProductsPage.module.css";

const ProductImg = ({ images, name }) => {
  const url = images?.[0]?.url;
  const [err, setErr] = useState(false);
  if (!url || err) return (
    <div className={styles.imgPlaceholder}>
      <span className={styles.imgPlaceholderIcon}>◈</span>
      <span className={styles.imgPlaceholderLetter}>{name?.charAt(0)?.toUpperCase()}</span>
    </div>
  );
  return <img src={url} alt={name} onError={() => setErr(true)} />;
};

const FilterForm = ({ filters, categories, onFilter, onClear }) => (
  <>
    <div className="form-group">
      <label className="label">Search</label>
      <input className="input" placeholder="Search products..." value={filters.search}
        onChange={e => onFilter("search", e.target.value)} />
    </div>
    <div className="form-group">
      <label className="label">Category</label>
      <select className="input" value={filters.category} onChange={e => onFilter("category", e.target.value)}>
        <option value="">All categories</option>
        {categories.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
      </select>
    </div>
    <div className="form-group">
      <label className="label">Min price (₹)</label>
      <input className="input" type="number" placeholder="0" value={filters.minPrice}
        onChange={e => onFilter("minPrice", e.target.value)} />
    </div>
    <div className="form-group">
      <label className="label">Max price (₹)</label>
      <input className="input" type="number" placeholder="Any" value={filters.maxPrice}
        onChange={e => onFilter("maxPrice", e.target.value)} />
    </div>
    <button className="btn btn-ghost" style={{ width: "100%", justifyContent: "center" }} onClick={onClear}>
      Clear filters
    </button>
  </>
);

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: "", minPrice: "", maxPrice: "", search: "" });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => { api.get("/products/categories").then(r => setCategories(r.data)); }, []);
  useEffect(() => { fetchProducts(); }, [filters, page]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
      const { data } = await api.get("/products", { params });
      setProducts(data.products);
      setTotalPages(data.pages);
    } finally { setLoading(false); }
  };

  const handleFilter = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setPage(1); };
  const handleClear = () => { setFilters({ category: "", minPrice: "", maxPrice: "", search: "" }); setPage(1); };

  return (
    <div className={styles.page}>
      {/* Desktop sidebar */}
      <div className={styles.sidebar}>
        <h3 className={styles.sideTitle}>Filters</h3>
        <FilterForm filters={filters} categories={categories} onFilter={handleFilter} onClear={handleClear} />
      </div>

      <div className={styles.main}>
        <div className={styles.topBar}>
          <h2 className={styles.pageTitle}>Products</h2>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ color: "var(--text3)", fontSize: "13px" }}>{products.length} items</span>
            {/* Mobile filter toggle */}
            <button className={styles.filterToggle} onClick={() => setShowMobileFilters(o => !o)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
              </svg>
              Filters
            </button>
          </div>
        </div>

        {/* Mobile filters panel */}
        <div className={`${styles.mobileFilters} ${showMobileFilters ? styles.open : ""}`}>
          <FilterForm filters={filters} categories={categories} onFilter={handleFilter} onClear={handleClear} />
        </div>

        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : products.length === 0 ? (
          <div className="empty-state"><h3>No products found</h3><p>Try different filters</p></div>
        ) : (
          <div className={styles.grid}>
            {products.map(p => (
              <div key={p._id} className={styles.productCard}>
                <div className={styles.cardImg}>
                  <ProductImg images={p.images} name={p.name} />
                  {p.stock === 0 && <div className={styles.oos}>Out of stock</div>}
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardCat}>{p.category}</div>
                  <div className={styles.cardName}>{p.name}</div>
                  {p.brand && <div className={styles.cardBrand}>{p.brand}</div>}
                  <div className={styles.cardFooter}>
                    <div className={styles.cardPrice}>₹{p.price?.toLocaleString()}</div>
                    <span className={p.stock > 0 ? "badge badge-green" : "badge badge-red"}>
                      {p.stock > 0 ? `${p.stock} left` : "Out"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button className="btn btn-ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span className={styles.pageInfo}>{page} / {totalPages}</span>
            <button className="btn btn-ghost" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}
